import { allowedState, type AnchorId } from '../anchors';

/**
 * Target resolution (CONTRACT §2): exactly ONE visible `[data-tour]` element in
 * the current responsive context.
 *
 * - An element may carry more than one anchor ID, space-separated
 *   (`data-tour="tenant.po.actions tenant.po.actions-phone"`), so the
 *   match is by token.
 * - "Visible" is what the browser renders: a copy inside a closed Sheet, an
 *   inactive tab panel or a `display: none` phone twin does not count.
 * - The wait is bounded — never more than four seconds — and abortable; it
 *   never sleeps a fixed time.
 */
export const MAX_WAIT_MS = 4000;

export type Resolution =
  | { status: 'found'; element: Element; anchor: AnchorId }
  | { status: 'ambiguous'; anchor: AnchorId }
  | { status: 'missing' };

export type VisibilityCheck = (element: Element) => boolean;

const SAFE_ID = /^[a-z0-9.-]+$/;

/** What the browser actually renders — not merely what is in the DOM. */
export function isRendered(element: Element): boolean {
  if (!element.isConnected) return false;

  if (
    'checkVisibility' in element &&
    !element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
  )
    return false;

  return [...element.getClientRects()].some((rect) => rect.width > 0 || rect.height > 0);
}

/** The first anchor, in order, with exactly one visible match. */
export function resolveTarget(
  anchors: readonly AnchorId[],
  visible: VisibilityCheck = isRendered,
  root: ParentNode = document,
): Resolution {
  let ambiguous: AnchorId | null = null;

  for (const anchor of anchors) {
    if (!SAFE_ID.test(anchor)) continue;

    const matches = [...root.querySelectorAll(`[data-tour~="${anchor}"]`)].filter(visible);

    if (matches.length === 1) return { status: 'found', element: matches[0], anchor };
    if (matches.length > 1 && ambiguous === null) ambiguous = anchor;
  }

  return ambiguous === null ? { status: 'missing' } : { status: 'ambiguous', anchor: ambiguous };
}

/** The state-probe attribute (anchors.ts `ANCHOR_STATES`). */
export const STATE_ATTRIBUTE = 'data-guidance-state';

/**
 * What the one visible element of `anchor` says about the server's state of the
 * thing it marks — `null` when the anchor is not showing, is ambiguous, carries
 * no state or a value this anchor may not carry. Pure DOM: no request.
 */
export function resolveState(
  anchor: AnchorId,
  visible: VisibilityCheck = isRendered,
  root: ParentNode = document,
): string | null {
  const result = resolveTarget([anchor], visible, root);

  if (result.status !== 'found') return null;

  return allowedState(anchor, result.element.getAttribute(STATE_ATTRIBUTE));
}

/** Whether a control says it cannot be used now (`disabled`, `aria-disabled`, or inside such). */
export function isDisabledTarget(element: Element): boolean {
  const control = element.matches('button, input, select, textarea, a, [role="button"]')
    ? element
    : element.querySelector('button, [role="button"]');

  // A control busy with the person's own action is working, not unavailable.
  if (control === null || control.getAttribute('aria-busy') === 'true') return false;

  return (
    (control as HTMLButtonElement).disabled === true ||
    control.getAttribute('aria-disabled') === 'true'
  );
}

function abortError(): DOMException {
  return new DOMException('The guide moved on.', 'AbortError');
}

/**
 * Resolves as soon as one anchor has exactly one visible match, or with the
 * last answer (missing / ambiguous) when the bounded wait runs out. Rejects
 * with an `AbortError` when aborted. Every observer and timer is released
 * however it ends.
 */
export function waitForTarget(
  anchors: readonly AnchorId[],
  options: {
    signal?: AbortSignal;
    timeoutMs?: number;
    visible?: VisibilityCheck;
    root?: ParentNode;
  } = {},
): Promise<Resolution> {
  const { signal, visible = isRendered, root = document } = options;
  const timeoutMs = Math.min(Math.max(options.timeoutMs ?? MAX_WAIT_MS, 0), MAX_WAIT_MS);

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());

      return;
    }

    let settled = false;
    const observer = new MutationObserver(() => check());
    // The observer catches DOM changes; a short, bounded re-check catches what
    // it cannot see — a stylesheet or media query showing a hidden twin.
    const interval = window.setInterval(() => check(), 100);
    const timer = window.setTimeout(() => finish(resolveTarget(anchors, visible, root)), timeoutMs);

    const release = () => {
      observer.disconnect();
      window.clearInterval(interval);
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };

    function finish(result: Resolution) {
      if (settled) return;

      settled = true;
      release();
      resolve(result);
    }

    function onAbort() {
      if (settled) return;

      settled = true;
      release();
      reject(abortError());
    }

    function check() {
      const result = resolveTarget(anchors, visible, root);

      if (result.status === 'found') finish(result);
    }

    signal?.addEventListener('abort', onAbort);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-tour', 'class', 'style', 'hidden', 'open'],
    });
    check();
  });
}
